import OSS from 'ali-oss'
import { queryAliStsTempToken } from '@/api/evaInfo'
import { defaultOssConfig } from '@/config/oss.config'
import type { AliStsToken } from '@/api/evaInfo/type'

export interface OssUploadContext<TPayload = unknown> {
  file: File | Blob
  payload: TPayload
  accountId?: string | number
  timestamp: number
  randomSuffix: string
  originalFileName: string
  ext: string
}

export interface OssUploadedContext<TPayload = unknown> {
  payload: TPayload
  accountId?: string | number
  objectName: string
  url: string
  ossResult: any
}

export interface UseOssUploadOptions<TPayload = unknown> {
  buildObjectName?: (ctx: OssUploadContext<TPayload>) => string
  resolveUrl?: (ctx: OssUploadedContext<TPayload>) => string
  onUploaded?: (ctx: OssUploadedContext<TPayload>) => Promise<void> | void
}

export interface OssUploadResult {
  url: string
  name: string
  res: any
  objectName: string
}

function normalizeUseOssUploadOptions<TPayload>(
  options?: boolean | UseOssUploadOptions<TPayload>
): UseOssUploadOptions<TPayload> {
  if (typeof options === 'boolean') {
    return {}
  }

  return options || {}
}

function getDefaultFileName(file: File | Blob, timestamp: number) {
  if (file instanceof File && file.name) {
    return file.name
  }

  return `${timestamp}.png`
}

function getFileExt(fileName: string) {
  const extIndex = fileName.lastIndexOf('.')
  return extIndex >= 0 ? fileName.slice(extIndex) : '.png'
}

function buildDefaultObjectName<TPayload>({
  accountId,
  timestamp,
  randomSuffix,
  ext
}: OssUploadContext<TPayload>) {
  return `upload/${accountId || 'anonymous'}/${timestamp}_${randomSuffix}${ext}`
}

export function useOssUpload<TPayload = unknown>(
  options?: boolean | UseOssUploadOptions<TPayload>
) {
  const resolvedOptions = normalizeUseOssUploadOptions(options)
  const stsToken = ref<AliStsToken | null>(null)
  const tokenExpireTime = ref<number>(0)
  const { accountId } = storeToRefs(useUserStore())
  const isTokenValid = computed(() => {
    if (!stsToken.value) return false
    const now = Date.now()
    return now < tokenExpireTime.value - 5 * 60 * 1000
  })

  const getStsToken = async () => {
    if (unref(isTokenValid)) {
      return unref(stsToken)
    }

    try {
      const data = await queryAliStsTempToken()

      if (!data) {
        throw new Error('STS 接口没有返回数据')
      }

      const credentials = data.body?.credentials || data

      if (!credentials || !credentials.accessKeyId) {
        throw new Error('STS 接口返回数据中找不到有效的凭证信息')
      }

      const bucket = data.body?.bucket || data.bucket || defaultOssConfig.bucket
      const region = data.body?.region || data.region || defaultOssConfig.region
      const endpoint = data.body?.endpoint || data.endpoint || defaultOssConfig.endpoint

      const result: AliStsToken = {
        accessKeyId: credentials.accessKeyId,
        accessKeySecret: credentials.accessKeySecret,
        securityToken: credentials.securityToken,
        expiration: credentials.expiration,
        bucket,
        region,
        endpoint
      }

      stsToken.value = result
      if (result.expiration) {
        tokenExpireTime.value = new Date(result.expiration).getTime()
      } else {
        tokenExpireTime.value = Date.now() + 60 * 60 * 1000
      }
      return result
    } catch (error: any) {
      console.error('获取 STS 凭证失败:', error?.message || error)
      throw error
    }
  }

  const createOssClient = async () => {
    const token = await getStsToken()

    if (!token) {
      throw new Error('无法获取 STS 凭证')
    }

    const ossConfig: any = {
      region: token.region || 'oss-cn-hangzhou',
      accessKeyId: token.accessKeyId,
      accessKeySecret: token.accessKeySecret,
      stsToken: token.securityToken,
      bucket: token.bucket,
      secure: true,
      refreshSTSToken: async () => {
        const newToken = await getStsToken()
        if (!newToken) {
          throw new Error('刷新 STS 凭证失败')
        }
        return {
          accessKeyId: newToken.accessKeyId,
          accessKeySecret: newToken.accessKeySecret,
          stsToken: newToken.securityToken
        }
      },
      refreshSTSTokenInterval: 300000
    }

    if (token.endpoint) {
      ossConfig.endpoint = token.endpoint
    }

    return new OSS(ossConfig)
  }

  const uploadToOss = async (file: File | Blob, payload: TPayload): Promise<OssUploadResult> => {
    try {
      const client = await createOssClient()
      const timestamp = Date.now()
      const randomSuffix = Math.random().toString(36).slice(2, 8)
      const originalFileName = getDefaultFileName(file, timestamp)
      const ext = getFileExt(originalFileName)
      const uploadContext: OssUploadContext<TPayload> = {
        file,
        payload,
        accountId: unref(accountId),
        timestamp,
        randomSuffix,
        originalFileName,
        ext
      }
      const objectName =
        resolvedOptions.buildObjectName?.(uploadContext) || buildDefaultObjectName(uploadContext)

      const result = await client.put(objectName, file, {
        headers: {
          'Content-Type': file.type || 'image/png'
        },
        timeout: 60000
      })

      const uploadedContext: OssUploadedContext<TPayload> = {
        payload,
        accountId: unref(accountId),
        objectName,
        url: result.url,
        ossResult: result
      }
      const resolvedUrl = resolvedOptions.resolveUrl?.(uploadedContext) || result.url

      if (resolvedOptions.onUploaded) {
        await resolvedOptions.onUploaded({
          ...uploadedContext,
          url: resolvedUrl
        })
      }

      return {
        url: resolvedUrl,
        name: result.name || objectName,
        res: result.res,
        objectName
      }
    } catch (error: any) {
      console.error('OSS 上传失败:', error?.message || error)
      throw error
    }
  }

  const uploadMonitorImageToOss = async (blob: Blob, payload: TPayload): Promise<string> => {
    const result = await uploadToOss(blob, payload)
    return result.url
  }

  return {
    uploadToOss,
    uploadMonitorImageToOss,
    getStsToken,
    isTokenValid
  }
}
